import {
  AccountTree,
  Dns,
  Image,
  KeyboardArrowRight,
  Link as LinkIcon,
  Pause,
  Science
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useProjectReadQuery, useProjectRunStats, useProjectStatsQuery } from '../api/project.js';
import { TestRunStatsTable } from '../components/TestRunTable.js';
import { useService } from '../providers/ServiceProvider.js';

interface MetricCardProps {
  icon: ReactNode;
  title: string;
  count: number;
  link: string;
}

export function ProjectView() {
  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const client = useService();

  const { data: project, isLoading: isProjectLoading } = useProjectReadQuery(projectId);
  const { data: stats, isLoading: isStatsLoading } = useProjectStatsQuery(projectId);
  const { data: runStats } = useProjectRunStats(projectId);

  if (isProjectLoading || isStatsLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress color="inherit" />
      </Paper>
    );
  }

  if (!project) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Project not found or failed to load.
        </Typography>
      </Paper>
    );
  }

  if (!stats) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Failed to load project stats.
        </Typography>
      </Paper>
    );
  }

  const handleDisableProject = async () => {
    try {
      const updatedEnabledState = !project.enabled;
      await client.projectUpdate(project.id, { enabled: updatedEnabledState });

      enqueueSnackbar(`${updatedEnabledState ? 'Enabled' : 'Disabled'} project successfully.`, {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar('Failed to update project state. Please try again.', { variant: 'error' });
    }
  };

  const MetricCard = ({ icon, title, count, link }: MetricCardProps) => (
    <Tooltip title={`View details for ${title}`} arrow>
      <Card variant="outlined" sx={{ height: '100%', backgroundColor: 'grey.100' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            {icon}
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          </Stack>
          <Typography variant="h4" sx={{ my: 2, textAlign: 'center' }}>
            {count}
          </Typography>
          <Button component={Link} to={link} endIcon={<LinkIcon />} fullWidth variant="outlined" size="small">
            View All
          </Button>
        </CardContent>
      </Card>
    </Tooltip>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Project Header */}
      <Card elevation={2} sx={{ p: 3, mb: 3, backgroundColor: 'primary.light' }}>
        <Typography variant="h4" color="primary.contrastText" gutterBottom>
          {project.name}
        </Typography>
        <Typography variant="body1" color="primary.contrastText">
          {project.description}
        </Typography>
        <Typography variant="body1" color="primary.contrastText">
          Status: {project.enabled ? 'Enabled' : 'Disabled'}
        </Typography>

        {project.tags?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1}>
              {project.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Card>

      {/* Quick Actions */}
      <Paper>
        <ButtonGroup fullWidth>
          <Button
            startIcon={project.enabled ? <Pause /> : <KeyboardArrowRight />}
            onClick={handleDisableProject}
          >
            {project.enabled ? 'Disable' : 'Enable'} Project
          </Button>
        </ButtonGroup>
      </Paper>

      {/* Metrics Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12}>
          {/* Run Stats */}
          {Array.isArray(runStats) && projectId && runStats.length > 0 && (
            <TestRunStatsTable projectId={projectId!} stats={runStats} />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Science color="primary" />}
            title="Tests"
            count={stats.tests}
            link={`/projects/${project.id}/tests`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Dns color="primary" />}
            title="Domains"
            count={stats.domains}
            link={`/projects/${project.id}/domains`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Image color="primary" />}
            title="Assets"
            count={stats.assets}
            link={`/projects/${project.id}/assets`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<AccountTree color="primary" />}
            title="Roots"
            count={stats.roots}
            link={`/projects/${project.id}/roots`}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
